import { ErrorHandler, inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';

/** One captured console error. */
export interface ICapturedError {
  at: string;
  message: string;

  /** First few frames only — enough to locate it, short enough not to dominate the payload. */
  stack?: string;
}

/** One captured failed HTTP call. Deliberately holds no body and no headers. */
export interface ICapturedRequest {
  at: string;
  method: string;
  url: string;
  status: number;

  /** The server's correlation id when it sent one, which is what makes a log searchable. */
  correlationId?: string;
}

/** Ring buffer sizes, per the spec: last 20 errors, last 10 failed calls. */
const MAX_ERRORS = 20;
const MAX_REQUESTS = 10;

/**
 * Keeps the last few console errors and failed HTTP calls so a bug report can carry them without the
 * reporter having to open devtools.
 *
 * **What this must never capture: request or response bodies, and auth headers.** That is a privacy
 * rule, not a size limit — these rows are read by BCT staff across every customer, and a captured body
 * could hold anything the user was working on. The shape of {@link ICapturedRequest} is the
 * enforcement: there is nowhere to put a body even by accident.
 *
 * Root-provided and always on. The buffers are two small arrays; the cost of keeping them is far lower
 * than the cost of asking a customer to reproduce a bug with devtools open.
 */
@Injectable({ providedIn: 'root' })
export class ConsoleCaptureService {
  private readonly errors: ICapturedError[] = [];
  private readonly requests: ICapturedRequest[] = [];

  recordError(error: unknown): void {
    const message = this.describe(error);
    if (!message) return;

    this.push(this.errors, {
      at: new Date().toISOString(),
      message,
      // Three frames. A full stack is mostly framework internals and crowds out the other 19 errors.
      stack: error instanceof Error && error.stack
        ? error.stack.split('\n').slice(0, 4).join('\n')
        : undefined,
    }, MAX_ERRORS);
  }

  recordFailedRequest(request: HttpRequest<unknown>, response: HttpErrorResponse): void {
    this.push(this.requests, {
      at: new Date().toISOString(),
      method: request.method,

      // Path only. The query string is dropped for the same reason ClientErrorReporter drops it:
      // it carries client data, and a bug report is read by BCT staff across every customer.
      url: this.trimUrl(request.url),
      status: response.status,
      correlationId:
        response.headers?.get('x-correlation-id')
        ?? response.headers?.get('x-request-id')
        ?? undefined,
    }, MAX_REQUESTS);
  }

  /** Oldest first, which is the order a developer reads them in. */
  snapshotErrors(): ICapturedError[] {
    return [...this.errors];
  }

  snapshotRequests(): ICapturedRequest[] {
    return [...this.requests];
  }

  /** Serialised for the API, which stores both as opaque JSON. Null when nothing was captured. */
  errorsJson(): string | null {
    return this.errors.length ? JSON.stringify(this.errors) : null;
  }

  requestsJson(): string | null {
    return this.requests.length ? JSON.stringify(this.requests) : null;
  }

  private push<T>(buffer: T[], entry: T, max: number): void {
    buffer.push(entry);
    if (buffer.length > max) buffer.splice(0, buffer.length - max);
  }

  private trimUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname;
    } catch {
      // Not a URL we can parse — keep it as-is rather than dropping the entry, since an unparseable
      // URL is itself worth seeing.
      return url;
    }
  }

  private describe(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      // Recorded as a request, not an error: the message would otherwise duplicate the entry and
      // HttpErrorResponse.message can embed the response body.
      return '';
    }
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;

    try {
      return JSON.stringify(error).slice(0, 500);
    } catch {
      return String(error);
    }
  }
}

/**
 * Records failed HTTP calls. Registered as a functional interceptor so it can sit last and see the
 * failure every other interceptor has already passed on.
 *
 * Rethrows untouched: this is an observer, and an interceptor that changed the error would make the
 * capture responsible for application behaviour.
 */
export function ticketingCaptureInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): ReturnType<HttpHandlerFn> {
  const capture = inject(ConsoleCaptureService);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        try {
          capture.recordFailedRequest(request, error);
        } catch {
          // Never let capture turn a handled failure into an unhandled one.
        }
      }
      return throwError(() => error);
    }),
  ) as ReturnType<HttpHandlerFn>;
}

/** Re-exported for the HttpEvent type the interceptor signature needs. */
export type { HttpEvent };
